<script setup lang="ts">
  import { icons } from '@gat-ship/icons'
  import { globalUtils } from '@gat-ship/utils'
  import { reactive, onMounted, computed, inject, watch, type Ref } from 'vue'
  import { useDialogZIndex } from '../../composables/useDialogZIndex'
  import { type GatButtonProps, GatButton } from '../gat-button'
  import { GatIcon } from '../gat-icon'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '../ui/alert-dialog'

  export interface GatAlertDialogProps {
    title?: string
    text?: string
    type?: 'default' | 'error'
    buttons?: {
      ok?: GatButtonProps | false
      cancel?: GatButtonProps | false
    }
  }

  const hasEvent = reactive({ clickCancel: false, clickSave: false })

  onMounted(() => {
    hasEvent.clickCancel =
      globalUtils.functions.components.hasEmitListener('click:cancel')
    hasEvent.clickSave =
      globalUtils.functions.components.hasEmitListener('click:ok')
  })

  const emit = defineEmits<{
    (e: 'update:open', openState: boolean): void
    (e: 'click:ok'): void
    (e: 'click:cancel'): void
  }>()

  const props = defineProps<GatAlertDialogProps>()

  const isOpen = defineModel('open', { default: false })

  const { zIndex } = useDialogZIndex(isOpen)

  const okButtonProps = computed((): GatButtonProps | undefined => {
    if (props.buttons?.ok === false) {
      return undefined
    }
    if (props.buttons?.ok) {
      return props.buttons.ok
    }
    if (hasEvent.clickSave) {
      return {
        text: 'Save Changes'
      }
    }
    return undefined
  })
  const cancelButtonProps = computed((): GatButtonProps | undefined => {
    if (props.buttons?.cancel === false) {
      return undefined
    }
    if (props.buttons?.cancel !== undefined) {
      return props.buttons.cancel
    }
    return {
      text: 'Cancel',
      variant: 'ghost'
    }
  })

  function cancelClicked() {
    const clickEvent = cancelButtonProps.value?.onClick
    if (hasEvent.clickCancel) {
      emit('click:cancel')
    } else if (clickEvent) {
      if (Array.isArray(clickEvent)) {
        clickEvent.forEach((ev) => ev())
      } else {
        clickEvent()
      }
    } else {
      emit('update:open', false)
    }
  }
</script>
<template>
  <AlertDialog :open="isOpen">
    <AlertDialogTrigger v-if="$slots.trigger" as-child>
      <slot name="trigger" />
    </AlertDialogTrigger>
    <AlertDialogContent :overlay-z-index="zIndex">
      <div>
        <div class="flex w-full items-center gap-2">
          <div
            v-if="type === 'error'"
            :class="[
              'flex h-10 w-10 items-center justify-center rounded-md'
              /* { 'bg-red-100': type === 'error' } */
            ]"
          >
            <gat-icon
              :icon="icons.common.error"
              :stroke-width="1.5"
              class="text-destructive"
            />
          </div>
          <div class="flex flex-col" :class="text ? 'gap-1.5' : ''">
            <AlertDialogTitle class="text-lg font-semibold leading-none">
              {{ title }}
            </AlertDialogTitle>
          </div>
        </div>
      </div>
      <slot :class="'text-sm leading-none'">
        <div class="text-sm leading-none">
          {{ text }}
        </div>
      </slot>
      <AlertDialogFooter>
        <slot name="footer">
          <gat-button
            v-if="cancelButtonProps"
            v-bind="cancelButtonProps"
            @click="cancelClicked()"
          />
          <gat-button
            v-if="okButtonProps"
            v-bind="okButtonProps"
            @click="[emit('click:ok'), okButtonProps.onClick]"
          />
        </slot>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
